import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui'
import { Textarea } from '../../ui/textarea'
import { createInventoryItem, updateInventoryItem, getOrgDeportes } from '../../../lib/supabase'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { useDeportes } from '../../../hooks/useDeportes'
import { opcionesDeporte } from '../../../lib/deportesOrg'

const CATEGORIA_OPTIONS = [
  { value: 'cono', label: 'Cono' },
  { value: 'balon', label: 'Balón' },
  { value: 'dummy', label: 'Dummy' },
  { value: 'casco', label: 'Casco' },
  { value: 'hombrera', label: 'Hombrera' },
  { value: 'proteccion', label: 'Protección' },
  { value: 'otro', label: 'Otro' },
]

const GENERAL_VALUE = '__general__'

const initialForm = {
  nombre: '',
  categoria: '',
  deporte_id: '',
  talla: '',
  modelo: '',
  ubicacion: '',
  notas: '',
}

const ItemFormModal = ({ open, onOpenChange, item = null, onSuccess }) => {
  const { org } = useOrganization()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [form, setForm] = useState(initialForm)
  const [deportes, setDeportes] = useState([])
  const { label: nombreDeporte } = useDeportes()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const isEditing = !!item

  // Solo los deportes activos de la organizacion (respeta el plan/licencia),
  // no el catalogo global.
  const loadDeportes = useCallback(async () => {
    if (!org?.id) return
    const { data } = await getOrgDeportes(org.id)
    setDeportes(data || [])
  }, [org?.id])

  useEffect(() => {
    if (open) loadDeportes()
  }, [open, loadDeportes])

  useEffect(() => {
    if (item) {
      setForm({
        nombre: item.nombre || '',
        categoria: item.categoria || '',
        deporte_id: item.deporte_id || '',
        talla: item.talla || '',
        modelo: item.modelo || '',
        ubicacion: item.ubicacion || '',
        notas: item.notas || '',
      })
    } else {
      setForm(initialForm)
    }
    setError(null)
    guard.resetDirty()
  }, [item, open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSelectChange = (field) => (value) => {
    guard.markDirty()
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio')
      return
    }

    setSaving(true)
    try {
      const payload = {
        nombre: form.nombre.trim(),
        categoria: form.categoria || null,
        deporte_id: form.deporte_id || null,
        talla: form.talla.trim() || null,
        modelo: form.modelo.trim() || null,
        ubicacion: form.ubicacion.trim() || null,
        notas: form.notas.trim() || null,
      }
      const result = isEditing
        ? await updateInventoryItem(item.id, payload)
        : await createInventoryItem(payload)
      if (result.error) throw result.error

      guard.resetDirty()
      onSuccess?.(result.data)
      onOpenChange(false)
    } catch (err) {
      console.error('[ItemFormModal]', err)
      setError(err?.message || 'No se pudo guardar el artículo')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar artículo' : 'Nuevo artículo'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              value={form.nombre}
              onChange={handleChange('nombre')}
              placeholder="Ej: Conos de entrenamiento"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="categoria">Categoría</Label>
              <Select value={form.categoria} onValueChange={handleSelectChange('categoria')}>
                <SelectTrigger id="categoria">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIA_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deporte_id">Deporte</Label>
              <Select
                value={form.deporte_id || GENERAL_VALUE}
                onValueChange={(v) => handleSelectChange('deporte_id')(v === GENERAL_VALUE ? '' : v)}
              >
                <SelectTrigger id="deporte_id">
                  <SelectValue placeholder="General" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GENERAL_VALUE}>General</SelectItem>
                  {opcionesDeporte(deportes, form.deporte_id, nombreDeporte).map((o) => (
                    <SelectItem key={o.value} value={o.value} disabled={o.deshabilitado}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="talla">Talla</Label>
              <Input
                id="talla"
                value={form.talla}
                onChange={handleChange('talla')}
                placeholder="Ej: M"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="modelo">Modelo</Label>
              <Input
                id="modelo"
                value={form.modelo}
                onChange={handleChange('modelo')}
                placeholder="Ej: Riddell Speed"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ubicacion">Ubicación</Label>
              <Input
                id="ubicacion"
                value={form.ubicacion}
                onChange={handleChange('ubicacion')}
                placeholder="Ej: Bodega A, estante 2"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notas">Notas</Label>
              <Textarea
                id="notas"
                value={form.notas}
                onChange={handleChange('notas')}
                placeholder="Notas adicionales"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !form.nombre.trim()}>
              {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear artículo'}
            </Button>
          </DialogFooter>
        </form>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default ItemFormModal
